import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './admin.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.romanvalderrabanoelcatador.com/api';
const DEFAULT_IMAGE = `${API_URL}/uploads/productos/default-product.jpg`;

const AdminProductos = () => {
    const [productos, setProductos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [selectedImages, setSelectedImages] = useState([]);
    const [previewImages, setPreviewImages] = useState([]);
    const [selectedPortada, setSelectedPortada] = useState(null);
    const [previewPortada, setPreviewPortada] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [carruselActivo, setCarruselActivo] = useState(null);
    const [imagenActualCarrusel, setImagenActualCarrusel] = useState(0);
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion_corta: '',
        descripcion_larga: '',
        precio: '',
        stock: '',
        categoria: '',
        origen: '',
        tostado: '',
        imagen_portada: '',
        destacado: false,
        activo: true,
        presentaciones: [],
        imagenes_extra: []
    });

    const categorias = [
        'Café',
        'Confitería de Café',
        'Licores',
        'Otros Productos'
    ];

    const tostados = ['Medio', 'Oscuro', 'Claro', 'Natural'];

    useEffect(() => {
        cargarProductos();
    }, []);

    const getImagenUrl = (filename) => {
        if (!filename) return DEFAULT_IMAGE;
        if (filename.startsWith('http')) return filename;
        return `${API_URL}/uploads/productos/${filename}`;
    };

    const cargarProductos = async () => {
        try {
            setLoading(true);
            const response = await api.get('/productos');
            
            const productosProcesados = response.data.map(producto => {
                let presentaciones = [];
                if (producto.presentaciones) {
                    if (Array.isArray(producto.presentaciones)) {
                        presentaciones = producto.presentaciones;
                    } else if (typeof producto.presentaciones === 'string') {
                        try {
                            presentaciones = JSON.parse(producto.presentaciones);
                        } catch (e) {
                            presentaciones = [];
                        }
                    }
                }

                let imagenesExtra = [];
                if (producto.imagenes_extra) {
                    if (Array.isArray(producto.imagenes_extra)) {
                        imagenesExtra = producto.imagenes_extra;
                    } else if (typeof producto.imagenes_extra === 'string') {
                        imagenesExtra = producto.imagenes_extra.split(',').filter(Boolean);
                    }
                }

                return {
                    ...producto,
                    presentaciones,
                    imagenes_extra: imagenesExtra
                };
            });
            
            setProductos(productosProcesados);
        } catch (error) {
            console.error('Error cargando productos:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleImageSelect = (e) => {
        const files = Array.from(e.target.files);
        setSelectedImages(prev => [...prev, ...files]);
        
        const newPreviews = files.map(file => URL.createObjectURL(file));
        setPreviewImages(prev => [...prev, ...newPreviews]);
    };

    const handlePortadaSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedPortada(file);
            setPreviewPortada(URL.createObjectURL(file));
        }
    };

    const removeImage = async (index) => {
        const imagenAEliminar = previewImages[index];
        
        // Si es una imagen guardada (URL del servidor)
        if (imagenAEliminar.startsWith(API_URL)) {
            const filename = imagenAEliminar.split('/').pop();
            
            // Preguntar antes de eliminar
            if (window.confirm('¿Eliminar esta imagen permanentemente?')) {
                try {
                    await api.delete(`/upload/productos/${filename}`);
                    console.log('✅ Imagen eliminada del servidor:', filename);
                    
                    // Eliminar del formData
                    setFormData(prev => ({
                        ...prev,
                        imagenes_extra: prev.imagenes_extra.filter((_, i) => {
                            const imgUrl = getImagenUrl(prev.imagenes_extra[i]);
                            return imgUrl !== imagenAEliminar;
                        })
                    }));
                } catch (error) {
                    console.error('❌ Error eliminando imagen:', error);
                    alert('Error al eliminar la imagen');
                    return;
                }
            } else {
                return; // Cancelar eliminación
            }
        }
        
        // Eliminar de preview
        setPreviewImages(prev => prev.filter((_, i) => i !== index));
        setSelectedImages(prev => prev.filter((_, i) => i !== index));
    };

    const removePortada = () => {
        if (previewPortada) {
            URL.revokeObjectURL(previewPortada);
        }
        setSelectedPortada(null);
        setPreviewPortada(null);
        setFormData(prev => ({ ...prev, imagen_portada: '' }));
    };

    const handlePresentacionChange = (index, field, value) => {
        setFormData(prev => {
            const presentaciones = Array.isArray(prev.presentaciones) ? prev.presentaciones : [];
            const nuevasPresentaciones = [...presentaciones];
            
            if (!nuevasPresentaciones[index]) {
                nuevasPresentaciones[index] = { tipo: '', precio_extra: 0 };
            }
            nuevasPresentaciones[index][field] = value;
            return { ...prev, presentaciones: nuevasPresentaciones };
        });
    };

    const agregarPresentacion = () => {
        setFormData(prev => ({
            ...prev,
            presentaciones: [...(prev.presentaciones || []), { tipo: '', precio_extra: 0 }]
        }));
    };

    const quitarPresentacion = (index) => {
        setFormData(prev => ({
            ...prev,
            presentaciones: (prev.presentaciones || []).filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUploading(true);
        
        try {
            let imagenPortadaUrl = formData.imagen_portada;
            if (selectedPortada) {
                const formDataPortada = new FormData();
                formDataPortada.append('imagen', selectedPortada);
                
                const portadaResponse = await api.post('/upload/portada', formDataPortada, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                
                if (portadaResponse.data.success) {
                    imagenPortadaUrl = portadaResponse.data.url;
                    console.log('✅ Portada subida:', imagenPortadaUrl);
                }
            }

            let imagenesExtraUrls = [...(formData.imagenes_extra || [])];
            if (selectedImages.length > 0) {
                const formDataImg = new FormData();
                selectedImages.forEach(img => {
                    formDataImg.append('imagenes', img);
                });
                
                const uploadResponse = await api.post('/upload/productos', formDataImg, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                
                if (uploadResponse.data.success) {
                    const nuevasUrls = uploadResponse.data.urls || [];
                    imagenesExtraUrls = [...imagenesExtraUrls, ...nuevasUrls];
                    console.log('✅ Imágenes extra subidas:', nuevasUrls);
                }
            }

            const productoData = {
                ...formData,
                precio: parseFloat(formData.precio) || 0,
                stock: parseInt(formData.stock) || 0,
                imagen_portada: imagenPortadaUrl,
                imagenes_extra: imagenesExtraUrls,
                presentaciones: formData.presentaciones || []
            };

            console.log('📤 Enviando producto:', productoData);

            if (editingId) {
                await api.put(`/productos/${editingId}`, productoData);
                alert('Producto actualizado exitosamente');
            } else {
                await api.post('/productos', productoData);
                alert('Producto creado exitosamente');
            }

            resetForm();
            cargarProductos();
            
        } catch (error) {
            console.error('❌ Error guardando producto:', error);
            alert('Error al guardar el producto: ' + (error.response?.data?.error || error.message));
        } finally {
            setUploading(false);
        }
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingId(null);
        setFormData({
            nombre: '',
            descripcion_corta: '',
            descripcion_larga: '',
            precio: '',
            stock: '',
            categoria: '',
            origen: '',
            tostado: '',
            imagen_portada: '',
            destacado: false,
            activo: true,
            presentaciones: [],
            imagenes_extra: []
        });
        
        // Limpiar imágenes seleccionadas
        previewImages.forEach(url => {
            if (url.startsWith('blob:')) {
                URL.revokeObjectURL(url);
            }
        });
        if (previewPortada && previewPortada.startsWith('blob:')) {
            URL.revokeObjectURL(previewPortada);
        }
        
        setSelectedImages([]);
        setPreviewImages([]);
        setSelectedPortada(null);
        setPreviewPortada(null);
    };

    const handleEdit = (producto) => {
        console.log('🖊️ Editando producto:', producto);
        setEditingId(producto.id);
        
        let presentacionesGuardadas = [];
        if (producto.presentaciones) {
            if (Array.isArray(producto.presentaciones)) {
                presentacionesGuardadas = producto.presentaciones;
            } else if (typeof producto.presentaciones === 'string') {
                try {
                    presentacionesGuardadas = JSON.parse(producto.presentaciones);
                } catch (e) {
                    presentacionesGuardadas = [];
                }
            }
        }

        setFormData({
            nombre: producto.nombre || '',
            descripcion_corta: producto.descripcion_corta || '',
            descripcion_larga: producto.descripcion_larga || '',
            precio: producto.precio || '',
            stock: producto.stock || '',
            categoria: producto.categoria || '',
            origen: producto.origen || '',
            tostado: producto.tostado || '',
            imagen_portada: producto.imagen_portada || '',
            destacado: producto.destacado || false,
            activo: producto.activo !== false,
            presentaciones: presentacionesGuardadas,
            imagenes_extra: producto.imagenes_extra || []
        });
        
        // 🔥 Cargar TODAS las imágenes extra guardadas para previsualización
        if (producto.imagenes_extra && producto.imagenes_extra.length > 0) {
            const imageUrls = producto.imagenes_extra.map(img => {
                const url = getImagenUrl(img);
                console.log('📸 Imagen guardada:', url);
                return url;
            });
            setPreviewImages(imageUrls);
            console.log('✅ Todas las imágenes extra cargadas:', imageUrls.length);
        } else {
            setPreviewImages([]);
            console.log('ℹ️ No hay imágenes extra guardadas');
        }

        // Cargar imagen de portada guardada
        if (producto.imagen_portada) {
            setPreviewPortada(getImagenUrl(producto.imagen_portada));
        } else {
            setPreviewPortada(null);
        }
        
        setSelectedImages([]);
        setSelectedPortada(null);
        setShowForm(true);
        window.scrollTo(0, 0);
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Estás seguro de eliminar este producto?')) {
            try {
                await api.delete(`/productos/${id}`);
                cargarProductos();
                alert('Producto eliminado');
            } catch (error) {
                console.error('Error eliminando producto:', error);
                alert('Error al eliminar producto');
            }
        }
    };

    const handleToggleDestacado = async (id, destacado) => {
        try {
            await api.patch(`/productos/${id}/destacado`, { destacado: !destacado });
            cargarProductos();
        } catch (error) {
            console.error('Error actualizando destacado:', error);
        }
    };

    const abrirCarrusel = (productoId, imagenes) => {
        setCarruselActivo(productoId);
        setImagenActualCarrusel(0);
    };

    const cerrarCarrusel = () => {
        setCarruselActivo(null);
        setImagenActualCarrusel(0);
    };

    const navegarCarrusel = (direccion) => {
        const producto = productos.find(p => p.id === carruselActivo);
        if (!producto || !producto.imagenes_extra) return;
        
        const total = producto.imagenes_extra.length;
        let nuevaPosicion = imagenActualCarrusel + direccion;
        
        if (nuevaPosicion < 0) nuevaPosicion = total - 1;
        if (nuevaPosicion >= total) nuevaPosicion = 0;
        
        setImagenActualCarrusel(nuevaPosicion);
    };

    const renderPresentaciones = (presentaciones) => {
        if (!presentaciones || presentaciones.length === 0) return null;
        
        let presentacionesArray = presentaciones;
        if (!Array.isArray(presentacionesArray)) {
            try {
                presentacionesArray = JSON.parse(presentaciones);
            } catch (e) {
                return null;
            }
        }
        
        if (presentacionesArray.length === 0) return null;
        
        return (
            <div className="producto-presentaciones">
                <small>Presentaciones:</small>
                <div className="presentaciones-tags">
                    {presentacionesArray.map((p, idx) => (
                        <span key={idx} className="presentacion-tag">
                            {p.tipo?.replace(/_/g, ' ') || 'N/A'}
                            {p.precio_extra > 0 && ` +$${p.precio_extra}`}
                        </span>
                    ))}
                </div>
            </div>
        );
    };

    if (loading) return <div className="loading">Cargando productos...</div>;

    return (
        <div className="admin-productos">
            {/* Carrusel Modal */}
            {carruselActivo && (
                <div className="carrusel-modal" onClick={cerrarCarrusel}>
                    <div className="carrusel-contenido" onClick={(e) => e.stopPropagation()}>
                        <button className="carrusel-cerrar" onClick={cerrarCarrusel}>×</button>
                        
                        {(() => {
                            const producto = productos.find(p => p.id === carruselActivo);
                            if (!producto || !producto.imagenes_extra || producto.imagenes_extra.length === 0) return null;
                            
                            const imagenActual = producto.imagenes_extra[imagenActualCarrusel];
                            const imagenUrl = getImagenUrl(imagenActual);
                            
                            return (
                                <>
                                    <div className="carrusel-imagen-container">
                                        <img 
                                            src={imagenUrl} 
                                            alt={`${producto.nombre} - Imagen ${imagenActualCarrusel + 1}`}
                                            className="carrusel-imagen"
                                        />
                                    </div>
                                    
                                    <div className="carrusel-controles">
                                        <button 
                                            className="carrusel-nav anterior" 
                                            onClick={() => navegarCarrusel(-1)}
                                        >
                                            ‹
                                        </button>
                                        <span className="carrusel-contador">
                                            {imagenActualCarrusel + 1} / {producto.imagenes_extra.length}
                                        </span>
                                        <button 
                                            className="carrusel-nav siguiente" 
                                            onClick={() => navegarCarrusel(1)}
                                        >
                                            ›
                                        </button>
                                    </div>
                                    
                                    <div className="carrusel-miniaturas">
                                        {producto.imagenes_extra.map((img, idx) => (
                                            <img
                                                key={idx}
                                                src={getImagenUrl(img)}
                                                alt={`Miniatura ${idx + 1}`}
                                                className={`carrusel-miniatura ${idx === imagenActualCarrusel ? 'activa' : ''}`}
                                                onClick={() => setImagenActualCarrusel(idx)}
                                            />
                                        ))}
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}

            <div className="page-header">
                <h1>Gestión de Productos</h1>
                <p>Categorías: Café · Confitería de Café · Licores · Otros Productos</p>
                <button 
                    onClick={() => {
                        if (showForm) {
                            resetForm();
                        } else {
                            setShowForm(true);
                        }
                    }} 
                    className="btn-primary"
                >
                    {showForm ? 'Cancelar' : '+ Nuevo Producto'}
                </button>
            </div>

            {showForm && (
                <form onSubmit={handleSubmit} className="producto-form">
                    <h2>{editingId ? 'Editar Producto' : 'Nuevo Producto'}</h2>
                    
                    <div className="form-grid">
                        {/* Campos básicos - igual que antes */}
                        <div className="form-group">
                            <label>Nombre del producto *</label>
                            <input
                                type="text"
                                name="nombre"
                                value={formData.nombre}
                                onChange={handleInputChange}
                                required
                                placeholder="Ej: Café Premium Altura"
                            />
                        </div>

                        <div className="form-group">
                            <label>Categoría *</label>
                            <select 
                                name="categoria" 
                                value={formData.categoria} 
                                onChange={handleInputChange}
                                required
                            >
                                <option value="">Seleccionar categoría</option>
                                {categorias.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Precio *</label>
                            <input
                                type="number"
                                name="precio"
                                value={formData.precio}
                                onChange={handleInputChange}
                                required
                                step="0.01"
                                min="0"
                            />
                        </div>

                        <div className="form-group">
                            <label>Stock *</label>
                            <input
                                type="number"
                                name="stock"
                                value={formData.stock}
                                onChange={handleInputChange}
                                required
                                min="0"
                            />
                        </div>

                        <div className="form-group">
                            <label>Origen</label>
                            <input
                                type="text"
                                name="origen"
                                value={formData.origen}
                                onChange={handleInputChange}
                                placeholder="Ej: Xicotepec, Puebla"
                            />
                        </div>

                        <div className="form-group">
                            <label>Tostado</label>
                            <select 
                                name="tostado" 
                                value={formData.tostado} 
                                onChange={handleInputChange}
                            >
                                <option value="">Seleccionar</option>
                                {tostados.map(t => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group full-width">
                            <label>Descripción Corta</label>
                            <input
                                type="text"
                                name="descripcion_corta"
                                value={formData.descripcion_corta}
                                onChange={handleInputChange}
                                placeholder="Breve descripción para tarjetas"
                                maxLength="100"
                            />
                        </div>

                        <div className="form-group full-width">
                            <label>Descripción Larga</label>
                            <textarea
                                name="descripcion_larga"
                                value={formData.descripcion_larga}
                                onChange={handleInputChange}
                                rows="4"
                                placeholder="Descripción detallada del producto"
                            />
                        </div>

                        <div className="form-check">
                            <label>
                                <input
                                    type="checkbox"
                                    name="destacado"
                                    checked={formData.destacado}
                                    onChange={handleInputChange}
                                />
                                Producto Destacado
                            </label>
                        </div>

                        <div className="form-check">
                            <label>
                                <input
                                    type="checkbox"
                                    name="activo"
                                    checked={formData.activo}
                                    onChange={handleInputChange}
                                />
                                Producto Activo
                            </label>
                        </div>

                        {/* SECCIÓN: Imagen de Portada */}
                        <div className="form-group full-width">
                            <label>Imagen de Portada (PNG, JPG)</label>
                            <div className="image-upload-area">
                                <input
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                    onChange={handlePortadaSelect}
                                    className="file-input"
                                />
                                <p className="file-input-hint">
                                    {previewPortada ? 'Reemplazar imagen' : 'Selecciona una imagen desde tu computadora'}
                                </p>
                                
                                {previewPortada && (
                                    <div className="preview-item portada-preview">
                                        <img 
                                            src={previewPortada} 
                                            alt="Vista previa portada"
                                        />
                                        <button 
                                            type="button" 
                                            onClick={removePortada}
                                            className="remove-image"
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* SECCIÓN: Presentaciones */}
                        <div className="form-group full-width">
                            <label>Presentaciones disponibles</label>
                            <div className="presentaciones-list">
                                {Array.isArray(formData.presentaciones) && formData.presentaciones.map((pres, index) => (
                                    <div key={index} className="presentacion-item">
                                        <select
                                            value={pres.tipo || ''}
                                            onChange={(e) => handlePresentacionChange(index, 'tipo', e.target.value)}
                                        >
                                            <option value="">Seleccionar</option>
                                            <option value="grano_medio">Grano - Medio Kg</option>
                                            <option value="grano_kilo">Grano - 1 Kg</option>
                                            <option value="molido_medio">Molido - Medio Kg</option>
                                            <option value="molido_kilo">Molido - 1 Kg</option>
                                            <option value="caja_6">Caja de 6</option>
                                            <option value="caja_12">Caja de 12</option>
                                            <option value="botella_750ml">Botella 750 ml</option>
                                            <option value="bolsa_250g">Bolsa 250g</option>
                                            <option value="bolsa_500g">Bolsa 500g</option>
                                        </select>
                                        <input
                                            type="number"
                                            placeholder="Precio extra"
                                            value={pres.precio_extra || 0}
                                            onChange={(e) => handlePresentacionChange(index, 'precio_extra', e.target.value)}
                                            min="0"
                                            step="0.01"
                                        />
                                        <button 
                                            type="button" 
                                            onClick={() => quitarPresentacion(index)}
                                            className="btn-remove"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                                <button 
                                    type="button" 
                                    onClick={agregarPresentacion}
                                    className="btn-add"
                                >
                                    + Agregar presentación
                                </button>
                            </div>
                        </div>

                        {/* SECCIÓN: Imágenes extra - AHORA SÍ MUESTRA TODAS */}
                        <div className="form-group full-width">
                            <label>Imágenes extra del producto (PNG, JPG)</label>
                            <div className="image-upload-area">
                                <input
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                    multiple
                                    onChange={handleImageSelect}
                                    className="file-input"
                                />
                                <p className="file-input-hint">
                                    {previewImages.length > 0 ? 'Agregar más imágenes' : 'Puedes seleccionar múltiples imágenes'}
                                </p>
                                
                                {/* 🔥 Grid que muestra TODAS las imágenes (guardadas + nuevas) */}
                                <div className="preview-grid">
                                    {previewImages.length > 0 ? (
                                        previewImages.map((img, index) => (
                                            <div key={index} className="preview-item">
                                                <img 
                                                    src={img} 
                                                    alt={`Imagen ${index + 1}`}
                                                />
                                                <button 
                                                    type="button" 
                                                    onClick={() => removeImage(index)}
                                                    className="remove-image"
                                                    title="Eliminar imagen"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="no-images-message">
                                            No hay imágenes extra. Selecciona archivos para subir.
                                        </div>
                                    )}
                                </div>
                                
                                {/* Contador de imágenes */}
                                {previewImages.length > 0 && (
                                    <div className="image-counter">
                                        Total: {previewImages.length} imagen(es)
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button 
                            type="submit" 
                            className="btn-primary btn-large"
                            disabled={uploading}
                        >
                            {uploading ? 'Subiendo imágenes...' : (editingId ? 'Actualizar Producto' : 'Guardar Producto')}
                        </button>
                    </div>
                </form>
            )}

            {/* Grid de productos */}
            <div className="productos-grid">
                {productos.map(producto => (
                    <div key={producto.id} className="producto-card">
                        <div className="producto-imagen">
                            <img 
                                src={getImagenUrl(producto.imagen_portada || producto.imagenes_extra?.[0])}
                                alt={producto.nombre}
                                onError={(e) => {
                                    console.error('❌ Error cargando imagen:', e.target.src);
                                    e.target.src = DEFAULT_IMAGE;
                                }}
                            />
                            {producto.destacado && (
                                <span className="badge-destacado">⭐ Destacado</span>
                            )}
                            
                            {producto.imagenes_extra && producto.imagenes_extra.length > 1 && (
                                <span className="badge-imagenes" onClick={() => abrirCarrusel(producto.id, producto.imagenes_extra)}>
                                    +{producto.imagenes_extra.length} imágenes
                                </span>
                            )}
                        </div>
                        
                        <div className="producto-info">
                            <h3>{producto.nombre}</h3>
                            <p className="producto-categoria">{producto.categoria}</p>
                            <p className="producto-descripcion">{producto.descripcion_corta}</p>
                            
                            <div className="producto-detalles">
                                <span className="producto-precio">${producto.precio}</span>
                                <span className={`producto-stock ${producto.stock < 5 ? 'bajo' : ''}`}>
                                    Stock: {producto.stock}
                                </span>
                            </div>

                            <div className="producto-origen">
                                {producto.origen && <span>📍 {producto.origen}</span>}
                                {producto.tostado && <span>🔥 {producto.tostado}</span>}
                            </div>

                            {renderPresentaciones(producto.presentaciones)}

                            {/* Miniaturas de imágenes extra */}
                            {producto.imagenes_extra && producto.imagenes_extra.length > 0 && (
                                <div className="producto-miniaturas">
                                    {producto.imagenes_extra.slice(0, 3).map((img, idx) => (
                                        <img
                                            key={idx}
                                            src={getImagenUrl(img)}
                                            alt={`${producto.nombre} ${idx + 1}`}
                                            className="producto-miniatura"
                                            onClick={() => abrirCarrusel(producto.id, producto.imagenes_extra)}
                                        />
                                    ))}
                                    {producto.imagenes_extra.length > 3 && (
                                        <span className="miniaturas-mas" onClick={() => abrirCarrusel(producto.id, producto.imagenes_extra)}>
                                            +{producto.imagenes_extra.length - 3}
                                        </span>
                                    )}
                                </div>
                            )}

                            <div className="producto-acciones">
                                <button 
                                    onClick={() => handleToggleDestacado(producto.id, producto.destacado)}
                                    className="btn-icon" 
                                    title={producto.destacado ? 'Quitar destacado' : 'Marcar destacado'}
                                >
                                    {producto.destacado ? '⭐' : '☆'}
                                </button>
                                <button 
                                    onClick={() => handleEdit(producto)}
                                    className="btn-icon" 
                                    title="Editar"
                                >
                                    ✏️
                                </button>
                                <button 
                                    onClick={() => handleDelete(producto.id)}
                                    className="btn-icon" 
                                    title="Eliminar"
                                >
                                    🗑️
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AdminProductos;